/**
 * @deprecated Legacy About Us page (Cosmos v1).
 *
 * As of the Cosmos v2 cutover, the canonical About Us at "/about" is served
 * by `landing-page/components/AboutUsNew.tsx` (new design). This file is no
 * longer routed to by default — it remains mounted at "/about-legacy" purely
 * as a rollback path. Once v2 is fully validated, this file can be safely
 * deleted.
 */
import React from 'react';
import { ArrowLeftIcon } from './icons';

interface AboutUsPageProps {
  onBack: () => void;
  onShowPrivacyPolicy?: () => void;
  onShowTermsOfService?: () => void;
}

interface TeamMember {
  name: string;
  role: string;
  description: string;
  linkedinUrl: string;
  imageUrl: string;
}

interface AdvisoryMember {
  name: string;
  location: string;
  description: string;
  imageUrl: string;
}

const teamMembers: TeamMember[] = [
  {
    name: "Parag Agarwal",
    role: "CEO / Product",
    description: "20 years in healthcare, innovation, and tech transfer across Scotland, Singapore, India",
    linkedinUrl: "https://www.linkedin.com/in/paragis/",
    imageUrl: "/images/team/parag.jpg"
  },
  {
    name: "Makarand Kaprekar", 
    role: "Commercial Head",
    description: "28+ years in senior business roles in MNCs. Exec, leadership coach to corporates and MSMEs.",
    linkedinUrl: "https://www.linkedin.com/in/makarandkaprekar/",
    imageUrl: "/images/team/makarand.jpg"
  },
  {
    name: "Pranav Garg",
    role: "Tech Lead", 
    description: "AI/ML and Python developer, has successfully worked on over 25+ LLM projects over 5 years.",
    linkedinUrl: "https://www.linkedin.com/in/prgarg007/",
    imageUrl: "/images/team/pranav.png"
  }
];

const advisoryMembers: AdvisoryMember[] = [
  {
    name: "Srikanth Sundararajan PhD",
    location: "India",
    description: "A veteran IITian - Innovator, Investor and Business Mentor with 35+ years global IT exp.",
    imageUrl: "/images/advisory-board/srikanth.jpg"
  },
  {
    name: "Dinesh Nambiar PhD", 
    location: "India",
    description: "30+ years heading R&D and quality management in Pharma & FMCG",
    imageUrl: "/images/advisory-board/dinesh.jpg"
  },
  {
    name: "Darshan Gangolli",
    location: "India", 
    description: "IP based SME IPOs, 30+ years financial services, real estate, infra development",
    imageUrl: "/images/advisory-board/darshan.jpg"
  },
  {
    name: "Rishikesh Trivedi",
    location: "UAE",
    description: "20+ years in tech investments and 15+ in strategy, marketing, alliances & sales",
    imageUrl: "/images/advisory-board/rishikesh.jpg"
  },
  {
    name: "Darren Griffin",
    location: "UK",
    description: "World-renowned geneticist, bioscientist with 30+ years industry liaison & science communications experience",
    imageUrl: "/images/advisory-board/darren.png"
  },
  {
    name: "Deborah Whittaker",
    location: "UK", 
    description: "PhD researcher in environmental biotech, academic board member, specialist in water quality",
    imageUrl: "/images/advisory-board/deborah.jpg"
  },
  {
    name: "Jateen Kore",
    location: "India", 
    description: "25+ years in digital and direct marketing. Ex leadership at Vodafone and IDFC Bank",
    imageUrl: "/images/advisory-board/jateen.jpg"
  }
];

const LinkedInIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

// SVG Icons for better visual appeal
const GlobalIcon = () => (
  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="m12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const AIIcon = () => (
  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="m2 17 10 5 10-5"/>
    <path d="m2 12 10 5 10-5"/>
  </svg>
);

const PartnershipIcon = () => (
  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="m22 21-3-3m0 0a4 4 0 0 0-7-3 4 4 0 0 0 3 7 4 4 0 0 0 3-7z"/>
  </svg>
);

export const AboutUsPage: React.FC<AboutUsPageProps> = ({ onBack, onShowPrivacyPolicy, onShowTermsOfService }) => {
  return (
    <div className="w-full h-full bg-[#FCFBF9] font-space-grotesk">
      {/* Header with Back Button */}
      <div className="w-full bg-white shadow-sm border-b border-primary-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
          >
            <ArrowLeftIcon />
            <span className="font-medium">Return to Cosmos Search</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold text-grayscale-1400 mb-6">
            About Us
          </h1>
          <p className="text-xl lg:text-2xl text-grayscale-1100 max-w-4xl mx-auto leading-relaxed">
            Our vision is to become the trusted destination for tech transfer globally.
          </p>
        </div>

        {/* Problem & Solution Section */}
        <div className="grid md:grid-cols-2 gap-12 mb-20">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-primary-200">
            <h2 className="text-2xl font-bold text-grayscale-1400 mb-6">
              What problems are we solving?
            </h2>
            <div className="space-y-4 text-grayscale-1100">
              <p className="flex items-start gap-3">
                <span className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></span>
                Bulk of university IP and research sits on the shelf and does not get commercialised.
              </p>
              <p className="flex items-start gap-3">
                <span className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></span>
                Industry, including small to medium & family owned businesses are under-exploiting available IP and research expertise.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-primary-200">
            <h2 className="text-2xl font-bold text-grayscale-1400 mb-6">
              How are we solving these?
            </h2>
            <p className="text-grayscale-1100 leading-relaxed">
              Starting with the world's first AI copilot for tech transfer to accelerate discovery and matchmaking between academic inventions and industry.
            </p>
          </div>
        </div>

        {/* Unique Value Proposition */}
        <div className="bg-gradient-to-r from-primary-100 to-primary-200 rounded-2xl p-8 lg:p-12 mb-20">
          <h2 className="text-2xl lg:text-3xl font-bold text-grayscale-1400 mb-8 text-center">
            How are we unique from other solutions and initiatives?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <GlobalIcon />
              </div>
              <h3 className="font-bold text-grayscale-1400 mb-3">Global Coverage</h3>
              <p className="text-grayscale-1100 text-sm leading-relaxed">
                Building the largest coverage of IP available for licensing globally, in a simple, easy to understand and comparable format.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <AIIcon />
              </div>
              <h3 className="font-bold text-grayscale-1400 mb-3">AI-Powered</h3>
              <p className="text-grayscale-1100 text-sm leading-relaxed">
                Our AI powered natural language search and messaging is highly targeted and personalised, providing the right amount of context to encourage action.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <PartnershipIcon />
              </div>
              <h3 className="font-bold text-grayscale-1400 mb-3">Strategic Partnerships</h3>
              <p className="text-grayscale-1100 text-sm leading-relaxed">
                Our partnerships are bringing in a critical mass of demand for IP from a previously untapped segment of small to medium sized and family owned businesses.
              </p>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="mb-20">
          <h2 className="text-2xl lg:text-3xl font-bold text-grayscale-1400 mb-8 text-center">
            What progress has been made?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-primary-200 text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">$30B</div>
              <p className="text-sm text-grayscale-1100">
                Engineering conglomerate matched with 24 technologies across 4 countries for wastewater treatment
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-primary-200 text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">1:4</div>
              <p className="text-sm text-grayscale-1100">
                From 50 early industry users, 1 in 4 found relevant matches for their problem statements
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-primary-200 text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">
                <svg className="w-8 h-8 mx-auto" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <p className="text-sm text-grayscale-1100">
                One of our matched solutions won their open innovation challenge and advanced to the PoC stage
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-primary-200 text-center">
              <div className="text-3xl font-bold text-primary-600 mb-2">10K+</div>
              <p className="text-sm text-grayscale-1100">
                Strategic partnership with German tech transfer pioneer, unlocking access to 10,000+ industry clients across 20 countries
              </p>
            </div>
          </div>
        </div>

        {/* Coverage Section */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg border border-primary-200 mb-20">
          <h2 className="text-2xl lg:text-3xl font-bold text-grayscale-1400 mb-8 text-center">
            Which technologies are we currently covering?
          </h2>
          <div className="text-center">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">25,100+</div>
                <div className="text-sm text-grayscale-1100">Technologies</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">34,400+</div>
                <div className="text-sm text-grayscale-1100">Researchers</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">28</div>
                <div className="text-sm text-grayscale-1100">Universities</div>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-grayscale-1100">
              <span className="bg-primary-100 px-3 py-1 rounded-full">MIT</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Stanford</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Purdue</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Arizona</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">UCLA</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Cornell</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Columbia</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Johns Hopkins</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Wisconsin-Madison</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Minnesota</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">IIT Bombay</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">IIT Madras</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">IIT Delhi</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Agrinnovate</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Arizona State University</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Max Planck Innovation</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Karlsruhe Institute of Technology</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">RWTH Aachen University</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Durham University</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Newcastle University</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">University of Pittsburgh</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Oxford University</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">State University of New York</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">University of Toronto</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">University of Pennsylvania</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">University of Maryland</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">University of California, San Diego</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Council of Scientific & Industrial Research (CSIR)</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">SRM Institute of Science & Technology (SRMIST)</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">The University of Sydney</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Monash University</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">The University of Melbourne</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Indian Institute of Science (IISc)</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">Vellore Institute of Technology (VIT)</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">University of Florida (UFI)</span>
              <span className="bg-primary-100 px-3 py-1 rounded-full">National Research Development Corporation (NRDC)</span>
            </div>
            {/* <div className="mt-6 p-4 bg-primary-50 rounded-lg">
              <h3 className="font-bold text-grayscale-1400 mb-2">What's coming next?</h3>
              <p className="text-grayscale-1100">Arizona State University</p>
            </div> */}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-20">
          <h2 className="text-2xl lg:text-3xl font-bold text-grayscale-1400 mb-12 text-center">
            What our users say
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-primary-200">
              <div className="flex items-start gap-4">
                <div className="text-4xl text-primary-600">"</div>
                <div>
                  <p className="text-grayscale-1100 mb-4 italic leading-relaxed">
                    Your platform is a game-changer for those of us passionate about biomaterials, sustainability, and technology. The way you bridge academic research with real-world applications is truly inspiring and essential for driving meaningful innovation.
                  </p>
                  <p className="text-sm font-medium text-grayscale-1400">
                    Promoter of certified compostable products manufacturer
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-primary-200">
              <div className="flex items-start gap-4">
                <div className="text-4xl text-primary-600">"</div>
                <div>
                  <p className="text-grayscale-1100 mb-4 italic leading-relaxed">
                    Your structured model of facilitating technology access from top global universities aligns seamlessly with our mission to foster East-West collaboration, particularly by connecting innovative solutions within our networks to international markets and vice versa.
                  </p>
                  <p className="text-sm font-medium text-grayscale-1400">
                    Director at €170mn+ tech transfer organisation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-20">
          <h2 className="text-2xl lg:text-3xl font-bold text-grayscale-1400 mb-12 text-center">
            Meet Our Team
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-primary-200 text-center hover:shadow-xl transition-shadow">
                <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden bg-primary-100">
                  <img 
                    src={member.imageUrl} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  <div className="hidden w-full h-full flex items-center justify-center text-4xl font-bold text-primary-600">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-grayscale-1400 mb-2">{member.name}</h3>
                <p className="text-primary-600 font-medium mb-3">{member.role}</p>
                <p className="text-sm text-grayscale-1100 leading-relaxed mb-4">{member.description}</p>
                <a 
                  href={member.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <LinkedInIcon />
                  <span className="text-sm font-medium">Connect on LinkedIn</span>
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Advisory Board */}
        <div className="mb-20">
          <h2 className="text-2xl lg:text-3xl font-bold text-grayscale-1400 mb-12 text-center">
            Advisory Board
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advisoryMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-primary-200 hover:shadow-xl transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-primary-100 flex-shrink-0">
                    <img 
                      src={member.imageUrl} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="hidden w-full h-full flex items-center justify-center text-sm font-bold text-primary-600">
                      {member.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-grayscale-1400 mb-1 text-sm">{member.name}</h3>
                    <p className="text-primary-600 text-xs font-medium mb-2">{member.location}</p>
                    <p className="text-xs text-grayscale-1100 leading-relaxed">{member.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Partnerships & Memberships */}
        <div className="mb-20">
          <h2 className="text-2xl lg:text-3xl font-bold text-grayscale-1400 mb-12 text-center">
          Partnerships & Memberships
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
            <div className="bg-white p-6 rounded-xl shadow-lg border border-primary-200 w-full flex items-center justify-center h-24">
              <img src="/images/partners/ifcci.png" alt="IFCCI CCI France-Inde" className="max-h-12 max-w-full object-contain" />
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg border border-primary-200 w-full flex items-center justify-center h-24">
              <img src="/images/partners/incubate-hub.png" alt="Incubate Hub" className="max-h-12 max-w-full object-contain" />
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg border border-primary-200 w-full flex items-center justify-center h-24">
              <img src="/images/partners/wework-labs.png" alt="WeWork Labs" className="max-h-12 max-w-full object-contain" />
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg border border-primary-200 w-full flex items-center justify-center h-24">
              <img src="/images/partners/steinbeis.png" alt="Steinbeis" className="max-h-12 max-w-full object-contain" />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="flex justify-center mb-20">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-primary-200 max-w-2xl w-full text-center">
            <h2 className="text-xl font-bold text-grayscale-1400 mb-4">
              I have a partnership idea, or would like to list my IP. Who do I contact?
            </h2>
            <p className="text-grayscale-1100 leading-relaxed">
              Please contact us at{' '}
              <a 
                href="mailto:info@universitytech.io" 
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                info@universitytech.io
              </a>
            </p>
          </div>
        </div>

        {/* LinkedIn Follow */}
        <div className="text-center bg-primary-100 rounded-2xl p-8 mb-16">
          <h2 className="text-xl font-bold text-grayscale-1400 mb-4">
            Stay Connected
          </h2>
          <p className="text-grayscale-1100 mb-6">
            Follow us on LinkedIn to get the latest news and updates.
          </p>
          <a 
            href="https://www.linkedin.com/company/university-tech" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-full font-medium transition-colors"
          >
            <LinkedInIcon />
            Follow us on LinkedIn
          </a>
        </div>

      </div>

      {/* Footer */}
      <footer className="w-full bg-primary-200 py-8">
        <div className="max-w-screen-xl mx-auto px-8">
          {/* Desktop Footer */}
          <div className="hidden md:flex justify-between items-center text-sm">
            <div className="flex items-center gap-4">
              <a 
                href="https://www.linkedin.com/company/university-tech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-black hover:text-primary-600 transition-colors"
              >
                <LinkedInIcon />
                Follow us on LinkedIn
              </a>
            </div>
            <div className="flex items-center gap-2 text-black">
              <span className="cursor-default">About Us</span>
              <span>|</span>
              <button 
                onClick={onShowTermsOfService}
                className="bg-transparent border-none cursor-pointer font-grotesk text-sm hover:text-primary-600 transition-colors"
              >
                Terms of service
              </button>
              <span>|</span>
              <button 
                onClick={onShowPrivacyPolicy}
                className="bg-transparent border-none cursor-pointer font-grotesk text-sm hover:text-primary-600 transition-colors"
              >
                Privacy Policy
              </button>
            </div>
          </div>

          {/* Mobile Footer */}
          <div className="md:hidden text-sm">
            <div className="flex flex-col items-center space-y-3">
              <span className="text-black cursor-default font-grotesk">
                About Us
              </span>
              <a 
                href="https://www.linkedin.com/company/university-tech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-black hover:text-primary-600 transition-colors"
              >
                <LinkedInIcon />
                Follow us on LinkedIn
              </a>
              <button 
                onClick={onShowTermsOfService}
                className="text-black hover:text-primary-600 transition-colors font-grotesk"
              >
                Terms of service
              </button>
              <button 
                onClick={onShowPrivacyPolicy}
                className="text-black hover:text-primary-600 transition-colors font-grotesk"
              >
                Privacy Policy
              </button>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-primary-300 text-center text-xs text-black">
            Copyright © 2024-2025 Paragise Private Limited. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUsPage;
